import React from 'react'
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  keyframes
} from '@mui/material'
import AudioFileIcon from '@mui/icons-material/AudioFile'
import VideoFileIcon from '@mui/icons-material/VideoFile'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import VisibilityIcon from '@mui/icons-material/Visibility'
import DownloadIcon from '@mui/icons-material/Download'
import DeleteIcon from '@mui/icons-material/Delete'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import StorageIcon from '@mui/icons-material/Storage'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import { FileItem } from '../services/api'

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

// Animação de ondas de som (transcrição em andamento)
const soundWave = keyframes`
  0%, 100% { transform: scaleY(0.3); }
  50% { transform: scaleY(1); }
`

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(0.95); }
`

// Formatação do tempo estimado
function formatEstimatedTime(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return ''
  
  if (seconds < 60) {
    return `~${seconds}s restantes`
  }
  
  const minutes = Math.ceil(seconds / 60)
  if (minutes === 1) {
    return '~1 min restante'
  }
  
  return `~${minutes} min restantes`
}

// Descrição do estado de processamento
function getProcessingStateDescription(state: string | null | undefined): string {
  switch (state) {
    case 'PENDING':
      return 'Aguardando...'
    case 'UPLOADING':
      return 'Enviando arquivo...'
    case 'TRANSCRIBING':
      return 'Transcrevendo áudio...'
    case 'PROCESSING_TRANSCRIPTION':
      return 'Processando transcrição...'
    case 'ANALYZING':
      return 'Analisando com IA...'
    case 'COMPLETED':
      return 'Concluído'
    case 'FAILED':
      return 'Falhou'
    default:
      return 'Processando...'
  }
}

type ViewMode = 'grid' | 'list'

type Props = {
  file: FileItem
  viewMode: ViewMode
  onOpen: (file: FileItem) => void
  onDelete: (fileId: string) => void
  onDownload: (fileId: string) => void
  // Selection props
  selectionMode?: boolean
  selected?: boolean
  onSelect?: (file: FileItem) => void
}

export default function FileItemCard({ 
  file, 
  viewMode, 
  onOpen, 
  onDelete, 
  onDownload,
  selectionMode = false,
  selected = false,
  onSelect
}: Props) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const menuOpen = Boolean(anchorEl)

  const isCompleted = file.status === 'completed'
  const isProcessing = file.status === 'processing'
  const isFailed = file.status === 'failed'
  const progress = typeof file.progress === 'number' ? file.progress : isCompleted ? 100 : 50

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    setAnchorEl(e.currentTarget)
  }

  const handleMenuClose = () => setAnchorEl(null)

  const handleAction = (action: () => void) => {
    handleMenuClose()
    action()
  }

  const FileIcon = () => {
    const type = file.type?.toLowerCase() || ''
    const title = file.title?.toLowerCase() || ''
    const iconSx = { fontSize: viewMode === 'grid' ? 48 : 32 }

    // Verificar por tipo ou extensão no título
    const audioTypes = ['audio', 'mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a', 'wma']
    const videoTypes = ['video', 'mp4', 'avi', 'mov', 'mkv', 'webm', 'wmv', 'flv', 'm4v']

    const isAudio = audioTypes.some(t => type.includes(t) || title.endsWith(`.${t}`))
    const isVideo = videoTypes.some(t => type.includes(t) || title.endsWith(`.${t}`))

    if (isAudio) {
      return <AudioFileIcon sx={{ ...iconSx, color: '#1976d2' }} />
    }
    if (isVideo) {
      return <VideoFileIcon sx={{ ...iconSx, color: '#9c27b0' }} />
    }
    return <InsertDriveFileIcon sx={{ ...iconSx, color: '#757575' }} />
  }

  const StatusBadge = () => {
    if (isCompleted) {
      return (
        <Tooltip title="Transcrição concluída">
          <CheckCircleIcon sx={{ fontSize: 18, color: '#4caf50' }} />
        </Tooltip>
      )
    }
    if (isFailed) {
      return (
        <Tooltip title="Erro no processamento">
          <ErrorIcon sx={{ fontSize: 18, color: '#f44336' }} />
        </Tooltip>
      )
    }
    // Animação de ondas de som para transcrição em andamento
    return (
      <Tooltip title={file.message || 'Transcrevendo...'}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '2px',
            height: 18,
            animation: `${pulse} 2s ease-in-out infinite`
          }}
        >
          {[0, 1, 2, 3, 4].map((i) => (
            <Box
              key={i}
              sx={{
                width: 3,
                height: '100%',
                bgcolor: '#ff9800',
                borderRadius: '1px',
                animation: `${soundWave} 0.8s ease-in-out infinite`,
                animationDelay: `${i * 0.1}s`,
                transformOrigin: 'bottom'
              }}
            />
          ))}
        </Box>
      </Tooltip>
    )
  }

  const formatBytes = (bytes: number) => {
    if (!bytes) return '—'
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1)
    const value = bytes / 1024 ** i
    return `${value.toFixed(value >= 10 ? 0 : 1)} ${sizes[i]}`
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—'
    // O backend retorna timestamp como string numérica (ex: "1769454102923")
    const timestamp = /^\d+$/.test(dateStr) ? parseInt(dateStr, 10) : Date.parse(dateStr)
    if (isNaN(timestamp)) return '—'
    
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) {
      return `Hoje, ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
    }
    if (diffDays === 1) {
      return `Ontem, ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
    }
    if (diffDays < 7) {
      return date.toLocaleDateString('pt-BR', { weekday: 'short', hour: '2-digit', minute: '2-digit' })
    }
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
  }

  // View em Grid (similar ao Drive)
  if (viewMode === 'grid') {
    const handleClick = () => {
      // Clique no card sempre abre análise (se concluído)
      // Seleção é feita apenas via checkbox
      if (isCompleted) {
        onOpen(file)
      }
    }

    return (
      <Box
        onClick={handleClick}
        sx={{
          bgcolor: selected 
            ? 'rgba(25, 118, 210, 0.04)' 
            : isCompleted 
              ? '#fff' 
              : '#fafafa',
          borderRadius: 3,
          border: selected 
            ? '2px solid #1976d2' 
            : isCompleted 
              ? '1px solid #c8e6c9'
              : isFailed
                ? '1px solid #ffcdd2'
                : '1px solid #e0e0e0',
          overflow: 'hidden',
          cursor: isCompleted ? 'pointer' : 'default',
          transition: 'all 0.2s ease',
          position: 'relative',
          boxShadow: isCompleted 
            ? '0 2px 8px rgba(76, 175, 80, 0.1)' 
            : 'none',
          '&:hover': {
            borderColor: isCompleted ? '#4caf50' : '#1976d2',
            boxShadow: isCompleted 
              ? '0 6px 20px rgba(76, 175, 80, 0.2)' 
              : '0 4px 12px rgba(25, 118, 210, 0.15)',
            transform: isCompleted ? 'translateY(-3px)' : 'translateY(-1px)',
            '& .file-actions': { opacity: 1 },
            '& .selection-checkbox': { opacity: 1 }
          }
        }}
      >
        {/* Selection checkbox - sempre visível no hover */}
        <Checkbox
          checked={selected}
          className="selection-checkbox"
          sx={{
            position: 'absolute',
            top: 4,
            left: 4,
            zIndex: 10,
            bgcolor: 'white',
            borderRadius: 1,
            opacity: selected ? 1 : 0,
            transition: 'opacity 0.2s',
            '&:hover': { bgcolor: 'white' },
            '& .MuiSvgIcon-root': { fontSize: 20 }
          }}
          onClick={(e) => {
            e.stopPropagation()
            if (onSelect) onSelect(file)
          }}
        />

        {/* Área do ícone */}
        <Box
          sx={{
            height: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: selected 
              ? 'rgba(25, 118, 210, 0.08)' 
              : isCompleted 
                ? 'linear-gradient(180deg, #e8f5e9 0%, #f5f5f5 100%)'
                : isFailed
                  ? '#fff5f5'
                  : '#f8f9fa',
            background: selected 
              ? 'rgba(25, 118, 210, 0.08)' 
              : isCompleted 
                ? 'linear-gradient(180deg, #e8f5e9 0%, #f5f5f5 100%)'
                : isFailed
                  ? '#fff5f5'
                  : '#f8f9fa',
            position: 'relative',
            borderBottom: isCompleted ? '2px solid #c8e6c9' : '1px solid #f0f0f0'
          }}
        >
          <FileIcon />

          {/* Status badge */}
          <Box sx={{ position: 'absolute', top: 8, left: 8 }}>
            <StatusBadge />
          </Box>

          {/* Menu button */}
          <Box
            className="file-actions"
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              opacity: 0,
              transition: 'opacity 0.2s'
            }}
          >
            <IconButton size="small" onClick={handleMenuOpen}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Progress bar para processando */}
          {isProcessing && (
            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, px: 1 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 3,
                  borderRadius: 1,
                  bgcolor: 'rgba(0,0,0,0.1)'
                }}
              />
            </Box>
          )}
        </Box>

        {/* Info do arquivo */}
        <Box sx={{ p: 1.5 }}>
          {/* Nome do arquivo */}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              mb: 0.75,
              color: '#333'
            }}
            title={file.title}
          >
            {file.title}
          </Typography>

          {/* Status chip */}
          <Box sx={{ mb: 1 }}>
            {isCompleted && (
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                label="Concluído"
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  bgcolor: '#e8f5e9',
                  color: '#2e7d32',
                  '& .MuiChip-icon': { color: '#2e7d32' }
                }}
              />
            )}
            {isFailed && (
              <Chip
                icon={<ErrorIcon sx={{ fontSize: 14 }} />}
                label="Erro"
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  bgcolor: '#ffebee',
                  color: '#c62828',
                  '& .MuiChip-icon': { color: '#c62828' }
                }}
              />
            )}
            {isProcessing && (
              <Box>
                <Chip
                  icon={<HourglassEmptyIcon sx={{ fontSize: 14 }} />}
                  label={getProcessingStateDescription(file.processingState)}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: '0.7rem',
                    bgcolor: '#fff3e0',
                    color: '#e65100',
                    '& .MuiChip-icon': { color: '#e65100' }
                  }}
                />
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    mt: 0.75,
                    height: 4,
                    borderRadius: 2,
                    bgcolor: 'rgba(255, 152, 0, 0.2)',
                    '& .MuiLinearProgress-bar': { bgcolor: '#ff9800' }
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
                  <Typography variant="caption" sx={{ color: '#ff9800', fontSize: '0.65rem' }}>
                    {progress}%
                  </Typography>
                  {file.estimatedSecondsRemaining && file.estimatedSecondsRemaining > 0 && (
                    <Typography variant="caption" sx={{ color: '#999', fontSize: '0.65rem' }}>
                      {formatEstimatedTime(file.estimatedSecondsRemaining)}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>

          {/* Metadados: Data e Tamanho */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarTodayIcon sx={{ fontSize: 12, color: '#999' }} />
              <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem' }}>
                {formatDate(file.createdAt)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <StorageIcon sx={{ fontSize: 12, color: '#999' }} />
              <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem' }}>
                {formatBytes(file.bytes)} • {file.type?.toUpperCase() || 'ARQUIVO'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Menu de ações */}
        <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose}>
          <MenuItem
            onClick={() => handleAction(() => onOpen(file))}
            disabled={!isCompleted}
          >
            <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Visualizar</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => handleAction(() => onDownload(file.fileId))}
            disabled={!isCompleted}
          >
            <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Baixar transcrição</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => handleAction(() => onDelete(file.fileId))}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Excluir</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    )
  }

  // View em Lista (similar ao Drive)
  const handleListClick = () => {
    // Clique no item sempre abre análise (se concluído)
    // Seleção é feita apenas via checkbox
    if (isCompleted) {
      onOpen(file)
    }
  }

  return (
    <Box
      onClick={handleListClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 1.5,
        borderBottom: '1px solid #f0f0f0',
        borderLeft: isCompleted ? '3px solid #4caf50' : isFailed ? '3px solid #f44336' : '3px solid transparent',
        cursor: isCompleted ? 'pointer' : 'default',
        transition: 'all 0.15s ease',
        bgcolor: selected 
          ? 'rgba(25, 118, 210, 0.04)' 
          : isCompleted 
            ? 'rgba(76, 175, 80, 0.02)'
            : 'transparent',
        '&:hover': {
          bgcolor: selected 
            ? 'rgba(25, 118, 210, 0.08)' 
            : isCompleted 
              ? 'rgba(76, 175, 80, 0.06)'
              : '#f5f9ff',
          '& .file-actions': { opacity: 1 },
          '& .selection-checkbox': { opacity: 1 }
        },
        '&:last-child': { borderBottom: 'none' }
      }}
    >
      {/* Selection checkbox - sempre visível no hover */}
      <Checkbox
        checked={selected}
        className="selection-checkbox"
        sx={{
          opacity: selected ? 1 : 0,
          transition: 'opacity 0.2s',
          '& .MuiSvgIcon-root': { fontSize: 20 }
        }}
        onClick={(e) => {
          e.stopPropagation()
          if (onSelect) onSelect(file)
        }}
      />

      {/* Ícone */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40 }}>
        <FileIcon />
      </Box>

      {/* Nome e status */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: '#333'
            }}
            title={file.title}
          >
            {file.title}
          </Typography>
          
          {/* Status Chip inline */}
          {isCompleted && (
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}
              label="Concluído"
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                bgcolor: '#e8f5e9',
                color: '#2e7d32',
                '& .MuiChip-icon': { color: '#2e7d32', ml: 0.5 },
                '& .MuiChip-label': { px: 0.75 }
              }}
            />
          )}
          {isFailed && (
            <Chip
              icon={<ErrorIcon sx={{ fontSize: 12 }} />}
              label="Erro"
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                bgcolor: '#ffebee',
                color: '#c62828',
                '& .MuiChip-icon': { color: '#c62828', ml: 0.5 },
                '& .MuiChip-label': { px: 0.75 }
              }}
            />
          )}
          {isProcessing && (
            <Chip
              icon={<HourglassEmptyIcon sx={{ fontSize: 12 }} />}
              label={getProcessingStateDescription(file.processingState)}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                bgcolor: '#fff3e0',
                color: '#e65100',
                '& .MuiChip-icon': { color: '#e65100', ml: 0.5 },
                '& .MuiChip-label': { px: 0.75 }
              }}
            />
          )}
        </Box>

        {isProcessing && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ 
                flex: 1, 
                maxWidth: 150, 
                height: 4, 
                borderRadius: 2,
                bgcolor: 'rgba(255, 152, 0, 0.2)',
                '& .MuiLinearProgress-bar': { bgcolor: '#ff9800' }
              }}
            />
            <Typography variant="caption" sx={{ color: '#ff9800', minWidth: 35, fontWeight: 500 }}>
              {progress}%
            </Typography>
            {file.estimatedSecondsRemaining && file.estimatedSecondsRemaining > 0 && (
              <Typography variant="caption" sx={{ color: '#999', fontSize: '0.7rem' }}>
                {formatEstimatedTime(file.estimatedSecondsRemaining)}
              </Typography>
            )}
          </Box>
        )}
        
        {/* Tipo de arquivo */}
        <Typography variant="caption" sx={{ color: '#999', fontSize: '0.7rem' }}>
          {file.type?.toUpperCase() || 'ARQUIVO'}
        </Typography>
      </Box>

      {/* Data */}
      <Typography variant="body2" sx={{ color: '#666', width: 140, flexShrink: 0 }}>
        {formatDate(file.createdAt)}
      </Typography>

      {/* Tamanho */}
      <Typography variant="body2" sx={{ color: '#666', width: 80, flexShrink: 0 }}>
        {formatBytes(file.bytes)}
      </Typography>

      {/* Ações */}
      <Box
        className="file-actions"
        sx={{ opacity: 0, transition: 'opacity 0.2s', display: 'flex', gap: 0.5 }}
      >
        <Tooltip title="Visualizar">
          <span>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onOpen(file) }}
              disabled={!isCompleted}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Baixar">
          <span>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onDownload(file.fileId) }}
              disabled={!isCompleted}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Excluir">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onDelete(file.fileId) }}
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )
}
